// Zod schemas for API request/response payloads. Used on both Worker and frontend
// for runtime validation and type inference.

import { z } from 'zod'
import { PACK_IDS } from './packs.ts'
import { companionIdSchema } from './companions.ts'

export const packIdSchema = z.enum(PACK_IDS)

/** kebab-case id used for items, topics, and variant_groups. */
export const kebabIdSchema = z
  .string()
  .min(1)
  .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'must be kebab-case ASCII')

/**
 * Item id used in artwork open/get payloads. Accepts either kebab-case
 * (regular items: `coat`, `silver-cup`) or the snake_case bonus form
 * (`joseph_panorama`). Bonus IDs are the codebase's only intentional
 * exception to kebab-case — see PACKS.bonus.id comment in packs.ts.
 */
export const artworkItemIdSchema = z
  .string()
  .min(1)
  .regex(
    /^[a-z0-9]+([-_][a-z0-9]+)*$/,
    'must be kebab-case or bonus_underscore form',
  )

export const loginByTokenSchema = z.object({
  // Personal short URL tokens are 4-char nanoid from a friendly alphabet.
  // Min 3 / max 16 keeps a sane bound while leaving headroom to grow.
  token: z.string().min(3).max(16),
})

// Forbid Unicode zero-width and bidi-override codepoints in display names:
//   - Zero-width / formatting (U+200B-200F, U+FEFF) can bypass `.min(1)` —
//     a "name" of one ZWSP looks empty in the UI.
//   - Bidi override / isolate (U+202A-202E, U+2066-2069) can flip the
//     rendered name on the share card canvas (e.g. evil masquerading as
//     a normal name reversed in place).
const UNICODE_DISALLOW = new RegExp(
  '[\\u200B-\\u200F\\u202A-\\u202E\\u2066-\\u2069\\uFEFF]',
)
const VISIBLE_CHAR = /\S/

// Minimal seed deny-list — names appear publicly on /share/:id cards so the
// floor is "what we'd be embarrassed for a parent to screenshot." Far from
// exhaustive; treat as triage until the alpha cohort proves the gaps.
// Substring match, case-insensitive — strict enough that "幹你娘" trips on
// "幹" but loose enough to ride out trivial l33t variants for now.
const PROFANITY_PATTERNS: readonly string[] = [
  '幹',
  '操你',
  '屌',
  '雞掰',
  '機掰',
  '北爛',
  '靠北',
  '靠杯',
  '三小',
  '白癡',
  '智障',
  'fuck',
  'shit',
  'cunt',
  'bitch',
  'nigger',
  'faggot',
]

function containsProfanity(value: string): boolean {
  const lower = value.toLowerCase()
  return PROFANITY_PATTERNS.some((p) => lower.includes(p.toLowerCase()))
}

const safeDisplayName = z
  .string()
  .trim()
  .min(1)
  .max(20)
  .regex(VISIBLE_CHAR, 'must contain a visible character')
  .refine((v) => !UNICODE_DISALLOW.test(v), 'control / bidi characters not allowed')
  .refine((v) => !containsProfanity(v), 'display name not allowed')

export const loginByCodeSchema = z.object({
  classCode: z.string().length(6),
  displayName: safeDisplayName,
})

// Self-onboarding for the alpha distribution model: the teacher shares one
// /welcome URL with the class, kids type a name, and we mint an account on
// the spot. Diverges from the spec's "pre-built hardcoded accounts" — see
// the Step 14.5 commit message and v0.1 validation report for the why.
export const quickSignupRequestSchema = z.object({
  displayName: safeDisplayName,
})

// Note: live artwork open/complete schemas are `artworkOpenRequestSchema` +
// `artworkCompleteRequestSchema` in practice.ts. Earlier draft schemas
// (createArtworkSchema / completeArtworkSchema) were removed Step 12 — they
// were dead code using the strict kebabIdSchema that rejects bonus
// snake_case ids; the live schemas use artworkItemIdSchema (above).

export const publicUserSchema = z.object({
  id: z.string(),
  displayName: z.string(),
  role: z.enum(['student', 'teacher']),
  churchName: z.string().nullable(),
  /** Remembered grace companion; null until the first-practice pick. */
  companionId: companionIdSchema.nullable(),
})
export type PublicUser = z.infer<typeof publicUserSchema>

export const quickSignupResponseSchema = z.object({
  user: publicUserSchema,
  shortToken: z.string(),
})
export type QuickSignupResponse = z.infer<typeof quickSignupResponseSchema>
